import React, { useState, useEffect } from "react";
import { useParams, Navigate, Link, useLocation } from "react-router-dom";
import ContactForm from "./ContactForm";
import Navbar from "./Navbar";
import ScrollToTopButton from "./ScrollToTop";
import Footer from "./Footer";
import { FaWhatsapp } from "react-icons/fa";
import { ToastContainer, toast, Bounce } from "react-toastify";
import { servicesData } from "../data/servicesData";
import Meta from "./Meta";
import {
  MessageSquare,
  ChevronDown,
  ChevronRight,
  Phone,
  Mail,
  ArrowRight,
  X,
  CheckCircle,
} from "lucide-react";

const ServicePage = () => {
  const { slug } = useParams();
  const [openFaq, setOpenFaq] = useState(null);
  const [selectedService, setSelectedService] = useState(slug);
  const [isFirstSubmission, setIsFirstSubmission] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleWhatsApp = () => {
    window.open("https://wa.me/919934743600", "_blank");
  };

  const handleEmail = () => {
    window.location.href = "mailto:govind@cagovindsinha.com";
  };

  const handlePhone = () => {
    window.location.href = "tel:+919934743600";
  };

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  // Check if service exists
  const service = servicesData[selectedService];
  if (!service) {
    return <Navigate to="/404" replace />;
  }

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!isFirstSubmission) {
      toast.warn("DO NOT SPAM THE BUTTON!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
        transition: Bounce,
      });
      await new Promise((resolve) => setTimeout(resolve, 10000));
      return;
    }

    setIsFirstSubmission(false);

    const form = e.target;
    const formData = new FormData(form);

    const name = formData.get("name");
    const email = formData.get("email");
    const phone = formData.get("phone");
    const originalMessage = formData.get("message");

    const combinedMessage = `
Phone: ${phone}

Message:
${originalMessage}
    `.trim();

    const finalFormData = new FormData();
    finalFormData.append("access_key", "371d5ae1-c163-4599-a34a-9a5e17b47cbe");
    finalFormData.append("email", email);
    finalFormData.append("message", combinedMessage);
    finalFormData.append("name", name);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: finalFormData,
      });

      const data = await response.json();
      if (data.success) {
        toast.success("Thank you for your inquiry! We will contact you soon.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        form.reset();
        setFormData({
          name: "",
          email: "",
          phone: "",
          service: slug || "",
          message: "",
        });
      } else {
        toast.error(data.message || "Something went wrong. Please try again.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        console.error("Error:", data);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Helper function to render HTML content safely
  const renderHTMLContent = (htmlString) => {
    return {
      __html: htmlString,
    };
  };

  // Helper function to render heading with appropriate styling
  const renderHeading = (type, text) => {
    if (!type || !text) return null;

    const baseClasses = "font-bold text-gray-800 mb-3";
    const headingClasses = {
      h1: "text-3xl " + baseClasses,
      h2: "text-2xl " + baseClasses,
      h3: "text-xl " + baseClasses,
      h4: "text-lg " + baseClasses,
      h5: "text-base " + baseClasses,
    };

    const HeadingTag = type;
    return (
      <HeadingTag className={headingClasses[type] || baseClasses}>
        {text}
      </HeadingTag>
    );
  };

  // Helper function to render bullet points - MODIFIED
  const renderBulletPoints = (bullets) => {
    if (!bullets || !Array.isArray(bullets) || bullets.length === 0)
      return null;

    return (
      <ul className="mb-6 space-y-2 text-gray-600">
        {bullets.map((bullet, index) => (
          <li key={index} className="flex items-start">
            <ArrowRight className="flex-shrink-0 w-4 h-4 mt-1 mr-2 text-blue-600" />
            <div // Changed from <span> to <div>
              className="leading-relaxed [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mt-2 [&_ul]:mb-2 [&_li]:mb-1 [&_b]:font-bold [&_strong]:font-bold" // Added [&_ul] and [&_li] for nested HTML
              dangerouslySetInnerHTML={renderHTMLContent(bullet)}
            />
          </li>
        ))}
      </ul>
    );
  };

  // Helper function to render table - MODIFIED for cell content
  const renderTable = (table) => {
    if (!table || !table.title) return null;

    return (
      <div className="p-6 mb-8 border border-gray-200 bg-gray-50 rounded-xl">
        <h3 className="mb-4 text-xl font-bold text-gray-800">{table.title}</h3>
        {table.headers &&
          Array.isArray(table.headers) &&
          table.headers.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full overflow-hidden border-collapse rounded-lg">
                <thead>
                  <tr className="text-white bg-gradient-to-r from-blue-600 to-purple-600">
                    {table.headers.map((header, headerIndex) => (
                      <th
                        key={headerIndex}
                        className="px-6 py-4 font-semibold text-left border border-gray-300"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                {table.rows &&
                  Array.isArray(table.rows) &&
                  table.rows.length > 0 && (
                    <tbody>
                      {table.rows.map((row, rowIndex) => {
                        if (!row || !Array.isArray(row)) return null;

                        return (
                          <tr
                            key={rowIndex}
                            className={
                              rowIndex % 2 === 0 ? "bg-gray-50" : "bg-white"
                            }
                          >
                            {row.map((cell, cellIndex) => (
                              <td
                                key={cellIndex}
                                className="px-6 py-4 text-gray-600 border border-gray-300"
                              >
                                <div
                                  dangerouslySetInnerHTML={renderHTMLContent(
                                    cell
                                  )}
                                />
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  )}
              </table>
            </div>
          )}
      </div>
    );
  };

  const renderProcess = (process) => {
    if (!process) return null;

    return (
      <div className="p-6 mb-8 border border-gray-200 bg-gray-50 rounded-xl">
        {process.title && (
          <h3 className="mb-4 text-xl font-bold text-gray-800">
            {process.title}
          </h3>
        )}

        {process.description && (
          <div className="mb-6">
            <div
              className="text-gray-600 leading-relaxed [&>br]:block [&>br]:my-2 [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mt-4 [&_ul]:mb-4 [&_li]:mb-2 [&_b]:font-bold [&_strong]:font-bold" // Changed [&>ul] to [&_ul] and [&>li] to [&_li]
              dangerouslySetInnerHTML={renderHTMLContent(process.description)}
            />
          </div>
        )}

        {process.steps &&
          Array.isArray(process.steps) &&
          process.steps.length > 0 && (
            <div className="relative space-y-6">
              <div className="absolute left-[24px] top-[6px] bottom-[32px] w-1 border-l-2 border-dashed border-yellow-500"></div>
              {process.steps.map((step, index) => {
                if (!step) return null;

                return (
                  <div key={index} className="relative flex items-start">
                    <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 mr-6 text-lg font-bold text-white rounded-full bg-gradient-to-r from-blue-600 to-purple-600">
                      {step.step || index + 1}
                    </div>
                    <div className="flex-1">
                      {step.title && (
                        <h4 className="mb-2 text-lg font-semibold text-gray-800">
                          {step.title}
                        </h4>
                      )}
                      {step.description && (
                        <div
                          className="leading-relaxed text-gray-600 [&>br]:block [&>br]:my-2 [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mt-2 [&_ul]:mb-2 [&_li]:mb-1 [&_b]:font-bold [&_strong]:font-bold" // Changed [&>ul] to [&_ul] and [&>li] to [&_li]
                          dangerouslySetInnerHTML={renderHTMLContent(
                            step.description
                          )}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>
    );
  };

  // Enhanced helper function to render overview content with table support
  const renderOverviewContent = (overview) => {
    if (!overview) return null;

    return (
      <div>
        {overview.title && (
          <h2 className="mb-4 text-3xl font-bold text-gray-800">
            {overview.title}
          </h2>
        )}

        {/* Render description if exists (backward compatibility) */}
        {overview.description && (
          <div className="mb-6">
            {Array.isArray(overview.description) ? (
              <ul className="space-y-2 text-lg leading-relaxed text-gray-600">
                {overview.description.map((item, idx) => (
                  <li key={idx} className="flex items-start">
                    <ArrowRight className="flex-shrink-0 w-4 h-4 mt-1 mr-2 text-blue-600" />
                    <span
                      className="whitespace-pre-wrap"
                      dangerouslySetInnerHTML={renderHTMLContent(item)}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <div
                className="text-lg leading-relaxed text-gray-600 [&>br]:block [&>br]:my-2 [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mt-4 [&_ul]:mb-4 [&_li]:mb-2 [&_b]:font-bold [&_strong]:font-bold" // Changed [&>ul] to [&_ul] and [&>li] to [&_li]
                dangerouslySetInnerHTML={renderHTMLContent(
                  overview.description
                )}
              />
            )}
          </div>
        )}

        {/* Render structured content if exists */}
        {overview.content &&
          Array.isArray(overview.content) &&
          overview.content.length > 0 && (
            <div className="space-y-6">
              {overview.content.map((contentItem, index) => {
                if (!contentItem) return null;

                // Handle table type content
                if (contentItem.type === "table") {
                  return <div key={index}>{renderTable(contentItem)}</div>;
                }

                // Handle process type content
                if (contentItem.type === "process") {
                  return <div key={index}>{renderProcess(contentItem)}</div>;
                }

                // Handle regular content with headings and bullets
                return (
                  <div
                    key={index}
                    className="p-4 pl-6 border-l-4 border-blue-500 rounded-r-lg bg-blue-50"
                  >
                    {contentItem.type &&
                      contentItem.text &&
                      renderHeading(contentItem.type, contentItem.text)}
                    {contentItem.bullets &&
                      renderBulletPoints(contentItem.bullets)}
                  </div>
                );
              })}
            </div>
          )}

        {/* Render standalone table if exists directly in overview (not inside content array) */}
        {overview.table && renderTable(overview.table)}

        {/* Render description_after_table if it exists */}
        {overview.description_after_table && (
          <div className="mt-6 mb-4 text-lg leading-relaxed text-gray-600">
            <div
              dangerouslySetInnerHTML={renderHTMLContent(
                overview.description_after_table
              )}
            />
          </div>
        )}

        {/* Render final_bullet if it exists */}
        {overview.final_bullet && (
          <div className="mt-4 mb-6 text-lg leading-relaxed text-gray-600">
            <div
              dangerouslySetInnerHTML={renderHTMLContent(overview.final_bullet)}
            />
          </div>
        )}

        {/* Render multiple tables if exists in overview (keeping for backward compatibility) */}
        {overview.tables &&
          Array.isArray(overview.tables) &&
          overview.tables.length > 0 && (
            <div className="space-y-6">
              {overview.tables.map((table, tableIndex) => (
                <div key={tableIndex}>{renderTable(table)}</div>
              ))}
            </div>
          )}
      </div>
    );
  };

  return (
    <>
      <Meta
        title={
          `${service.hero?.title} - Govind Sinha & Co.` ||
          "Service Details - Govind Sinha & Co."
        }
        description={
          service.hero?.description || "Learn more about our services."
        }
        canonical={`https://cagovindsinha.com/services/${slug}`}
      />
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <ToastContainer className="z-[100]" />
        <div className="fixed z-50 bottom-6 right-6">
          {/* Contact Options */}
          <div
            className={`flex flex-col-reverse items-end space-y-reverse space-y-4 mb-4 transition-all duration-500 ease-out ${
              isOpen
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8 pointer-events-none"
            }`}
          >
            {/* WhatsApp */}
            <div className="relative group">
              <button
                onClick={handleWhatsApp}
                className="flex items-center justify-center text-white transition-all duration-300 transform rounded-full shadow-lg w-14 h-14 bg-gradient-to-r from-green-500 to-green-600 hover:shadow-xl hover:scale-110 hover:from-green-600 hover:to-green-700"
              >
                <FaWhatsapp className="w-6 h-6" />
              </button>
              <div className="absolute transition-opacity duration-300 transform -translate-y-1/2 opacity-0 pointer-events-none right-16 top-1/2 group-hover:opacity-100">
                <div className="px-3 py-1 text-sm text-white bg-gray-900 rounded-lg whitespace-nowrap">
                  WhatsApp
                  <div className="absolute transform -translate-y-1/2 border-4 border-transparent left-full top-1/2 border-l-gray-900"></div>
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="relative group">
              <button
                onClick={handleEmail}
                className="flex items-center justify-center text-white transition-all duration-300 transform rounded-full shadow-lg w-14 h-14 bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-110 hover:from-blue-600 hover:to-blue-700"
              >
                <Mail className="w-6 h-6" />
              </button>
              <div className="absolute transition-opacity duration-300 transform -translate-y-1/2 opacity-0 pointer-events-none right-16 top-1/2 group-hover:opacity-100">
                <div className="px-3 py-1 text-sm text-white bg-gray-900 rounded-lg whitespace-nowrap">
                  Email
                  <div className="absolute transform -translate-y-1/2 border-4 border-transparent left-full top-1/2 border-l-gray-900"></div>
                </div>
              </div>
            </div>

            {/* Phone */}
            <div className="relative group">
              <button
                onClick={handlePhone}
                className="flex items-center justify-center text-white transition-all duration-300 transform rounded-full shadow-lg w-14 h-14 bg-gradient-to-r from-purple-500 to-purple-600 hover:shadow-xl hover:scale-110 hover:from-purple-600 hover:to-purple-700"
              >
                <Phone className="w-6 h-6" />
              </button>
              <div className="absolute transition-opacity duration-300 transform -translate-y-1/2 opacity-0 pointer-events-none right-16 top-1/2 group-hover:opacity-100">
                <div className="px-3 py-1 text-sm text-white bg-gray-900 rounded-lg whitespace-nowrap">
                  Call Us
                  <div className="absolute transform -translate-y-1/2 border-4 border-transparent left-full top-1/2 border-l-gray-900"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Toggle Button */}
          <button
            onClick={toggleMenu}
            className={`flex items-center justify-center w-16 h-16 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full shadow-2xl hover:shadow-3xl transform transition-all duration-500 ease-out hover:scale-105 ${
              isOpen ? "rotate-180" : "rotate-0"
            }`}
          >
            <div
              className={`transition-transform duration-300 ${
                isOpen ? "rotate-180" : "rotate-0"
              }`}
            >
              {isOpen ? (
                <X className="w-7 h-7" />
              ) : (
                <MessageSquare className="w-7 h-7" />
              )}
            </div>
          </button>

          {/* Backdrop blur when open */}
          {isOpen && (
            <div
              className="fixed inset-0 bg-black/5 backdrop-blur-[1px] -z-10"
              onClick={toggleMenu}
            />
          )}
        </div>

        <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 mt-16 lg:grid-cols-3">
            {/* Main Content */}
            <div className="space-y-8 lg:col-span-2">
              {/* Hero Section */}
              <div className="p-8 text-white shadow-2xl bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl">
                {service.hero?.title && (
                  <h1 className="mb-4 text-4xl font-bold">
                    {service.hero.title}
                  </h1>
                )}
                {service.hero?.description && (
                  <p
                    className="mb-8 text-xl text-blue-100"
                    dangerouslySetInnerHTML={renderHTMLContent(
                      service.hero.description
                    )}
                  ></p>
                )}

                <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                  {service.hero?.advantages &&
                    Array.isArray(service.hero.advantages) &&
                    service.hero.advantages.length > 0 && (
                      <div>
                        <h3 className="flex items-center mb-4 text-2xl font-semibold">
                          <CheckCircle className="w-6 h-6 mr-2" />
                          Key Advantages
                        </h3>
                        <ul className="space-y-2">
                          {service.hero.advantages.map((advantage, index) => (
                            <li key={index} className="flex items-start">
                              <ArrowRight className="w-4 h-4 mt-1 mr-2 text-blue-300" />
                              <span className="text-blue-100">{advantage}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                  {service.hero?.eligibleCriteria &&
                    Array.isArray(service.hero.eligibleCriteria) &&
                    service.hero.eligibleCriteria.length > 0 && (
                      <div>
                        <h3 className="flex items-center mb-4 text-2xl font-semibold">
                          <CheckCircle className="w-6 h-6 mr-2" />
                          Eligible Criteria
                        </h3>
                        <ul className="space-y-2">
                          {service.hero.eligibleCriteria.map(
                            (criteria, index) => (
                              <li key={index} className="flex items-start">
                                <ArrowRight className="w-4 h-4 mt-1 mr-2 text-blue-300" />
                                <span className="text-blue-100">
                                  {criteria}
                                </span>
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                </div>
              </div>

              {/* Overview */}
              {service.content?.overviews &&
                Array.isArray(service.content.overviews) &&
                service.content.overviews.length > 0 && (
                  <div className="p-8 bg-white border border-gray-100 shadow-lg rounded-2xl">
                    {service.content.overviews.map((overview, index) => (
                      <div
                        key={index}
                        className={
                          index > 0 ? "mt-8 pt-8 border-t border-gray-100" : ""
                        }
                      >
                        {renderOverviewContent(overview)}
                      </div>
                    ))}

                    {service.content?.features &&
                      Array.isArray(service.content.features) &&
                      service.content.features.length > 0 && (
                        <div className="grid grid-cols-1 gap-4 mt-8 md:grid-cols-2">
                          {service.content.features.map((feature, index) => (
                            <div
                              key={index}
                              className="flex items-center p-4 rounded-lg bg-blue-50"
                            >
                              <CheckCircle className="w-5 h-5 mr-3 text-blue-600" />
                              <span className="text-gray-700">{feature}</span>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                )}

              {/* Dynamic Tables (keeping for backward compatibility) */}
              {service.content?.tables &&
                Array.isArray(service.content.tables) &&
                service.content.tables.length > 0 &&
                service.content.tables.map((table, tableIndex) => {
                  if (!table || !table.title) return null;

                  return (
                    <div
                      key={tableIndex}
                      className="p-8 bg-white border border-gray-100 shadow-lg rounded-2xl"
                    >
                      <h2 className="mb-6 text-3xl font-bold text-gray-800">
                        {table.title}
                      </h2>
                      {table.headers &&
                        Array.isArray(table.headers) &&
                        table.headers.length > 0 && (
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                              <thead>
                                <tr className="text-white bg-gradient-to-r from-blue-600 to-purple-600">
                                  {table.headers.map((header, headerIndex) => (
                                    <th
                                      key={headerIndex}
                                      className="px-6 py-4 font-semibold text-left border border-gray-300"
                                    >
                                      {header}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              {table.rows &&
                                Array.isArray(table.rows) &&
                                table.rows.length > 0 && (
                                  <tbody>
                                    {table.rows.map((row, rowIndex) => {
                                      if (!row || !Array.isArray(row))
                                        return null;

                                      return (
                                        <tr
                                          key={rowIndex}
                                          className={
                                            rowIndex % 2 === 0
                                              ? "bg-gray-50"
                                              : "bg-white"
                                          }
                                        >
                                          {row.map((cell, cellIndex) => (
                                            <td
                                              key={cellIndex}
                                              className="px-6 py-4 text-gray-600 border border-gray-300"
                                            >
                                              <div
                                                dangerouslySetInnerHTML={renderHTMLContent(
                                                  cell
                                                )}
                                              />
                                            </td>
                                          ))}
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                )}
                            </table>
                          </div>
                        )}
                    </div>
                  );
                })}

              {/* Process Steps (keeping for backward compatibility) */}
              {/* Note: The process section is now primarily handled within overviews, 
                but this block is kept for any potential standalone process data. */}
              {service.content?.process && (
                <div className="p-8 bg-white border border-gray-100 shadow-lg rounded-2xl">
                  {service.content.process.title && (
                    <h2 className="mb-8 text-3xl font-bold text-gray-800">
                      {service.content.process.title}
                    </h2>
                  )}

                  {service.content.process.description && (
                    <div className="mb-8">
                      <div
                        className="text-lg leading-relaxed [&>br]:block [&>br]:my-2 [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mt-4 [&_ul]:mb-4 [&_li]:mb-2 [&_b]:font-bold [&_strong]:font-bold" // Changed [&>ul] to [&_ul] and [&>li] to [&_li]
                        dangerouslySetInnerHTML={renderHTMLContent(
                          service.content.process.description
                        )}
                      />
                    </div>
                  )}

                  {service.content.process.steps &&
                    Array.isArray(service.content.process.steps) &&
                    service.content.process.steps.length > 0 && (
                      <div className="relative space-y-6">
                        <div className="absolute left-[24px] top-[6px] bottom-[32px] w-1 border-l-2 border-dashed border-yellow-500"></div>
                        {service.content.process.steps.map((step, index) => {
                          if (!step) return null;

                          return (
                            <div
                              key={index}
                              className="relative flex items-start"
                            >
                              <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 mr-6 text-lg font-bold text-white rounded-full bg-gradient-to-r from-blue-600 to-purple-600">
                                {step.step || index + 1}
                              </div>
                              <div className="flex-1">
                                {step.title && (
                                  <h3 className="mb-2 text-xl font-semibold text-gray-800">
                                    {step.title}
                                  </h3>
                                )}
                                {step.description && (
                                  <div
                                    className="leading-relaxed text-gray-600 [&>br]:block [&>br]:my-2 [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mt-2 [&_ul]:mb-2 [&_li]:mb-1 [&_b]:font-bold [&_strong]:font-bold" // Changed [&>ul] to [&_ul] and [&>li] to [&_li]
                                    dangerouslySetInnerHTML={renderHTMLContent(
                                      step.description
                                    )}
                                  />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                </div>
              )}

              {/* FAQ Section */}
              {service.content?.faq &&
                Array.isArray(service.content.faq) &&
                service.content.faq.length > 0 && (
                  <div className="p-8 bg-white border border-gray-100 shadow-lg rounded-2xl">
                    <h2 className="mb-6 text-3xl font-bold text-gray-800">
                      Frequently Asked Questions
                    </h2>

                    <div className="space-y-4">
                      {service.content.faq.map((faq, index) => {
                        if (!faq || !faq.question) return null;

                        return (
                          <div
                            key={index}
                            className="border border-gray-200 rounded-lg"
                          >
                            <button
                              onClick={() =>
                                setOpenFaq(openFaq === index ? null : index)
                              }
                              className="flex items-center justify-between w-full p-6 text-left transition-colors hover:bg-gray-50"
                            >
                              <span className="font-semibold text-gray-800">
                                {faq.question}
                              </span>
                              {openFaq === index ? (
                                <ChevronDown className="w-5 h-5 text-gray-500" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-gray-500" />
                              )}
                            </button>
                            {openFaq === index && faq.answer && (
                              <div className="px-6 pb-6">
                                <div
                                  className="leading-relaxed text-gray-600 [&>br]:block [&>br]:my-2 [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mt-2 [&_ul]:mb-2 [&_li]:mb-1 [&_b]:font-bold [&_strong]:font-bold" // Changed [&>ul] to [&_ul] and [&>li] to [&_li]
                                  dangerouslySetInnerHTML={renderHTMLContent(
                                    faq.answer
                                  )}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
            </div>

            {/* Contact Form Sidebar */}
            <div className="lg:col-span-1">
              <ContactForm
                formData={formData}
                handleInputChange={handleInputChange}
                handleFormSubmit={handleFormSubmit}
                slug={slug}
              />
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default ServicePage;
